import { useCallback, useEffect, useRef, useState } from "react";
import { reportError } from "../lib/reportError";

const useAudioEvents = (audio, next) => {
  const [state, setState] = useState({ time: 0, duration: 0, isPlaying: false, error: "" });
  useEffect(() => {
    const events = {
      timeupdate: () => setState(current => ({ ...current, time: audio.currentTime })),
      loadedmetadata: () => setState(current => ({ ...current, duration: Number.isFinite(audio.duration) ? audio.duration : 0 })),
      play: () => setState(current => ({ ...current, isPlaying: true, error: "" })),
      pause: () => setState(current => ({ ...current, isPlaying: false })),
      emptied: () => setState({ time: 0, duration: 0, isPlaying: false, error: "" }),
      ended: next,
      error: () => setState(current => ({ ...current, isPlaying: false, error: "Playback is unavailable. Please try another track." })),
    };
    Object.entries(events).forEach(([name, handler]) => audio.addEventListener(name, handler));
    return () => Object.entries(events).forEach(([name, handler]) => audio.removeEventListener(name, handler));
  }, [audio, next]);
  return state;
};

const play = audio => audio.play().catch(error => reportError("Audio playback could not start", error));

export const useAudioPlayer = tracks => {
  const [audio] = useState(() => new Audio());
  const [index, setIndex] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [muted, setMuted] = useState(false);
  const autoplay = useRef(false);
  const track = tracks[index];
  const src = track?.src;
  const count = tracks.length;
  const next = useCallback(() => {
    if (!count) return;
    if (count === 1) { audio.currentTime = 0; void play(audio); return; }
    autoplay.current = true;
    setIndex(current => (current + 1) % count);
  }, [audio, count]);
  const prev = useCallback(() => {
    if (!count) return;
    if (count === 1) { audio.currentTime = 0; void play(audio); return; }
    autoplay.current = true;
    setIndex(current => (current - 1 + count) % count);
  }, [audio, count]);
  const state = useAudioEvents(audio, next);
  useEffect(() => { audio.volume = volume; audio.muted = muted; }, [audio, volume, muted]);
  useEffect(() => {
    if (!src) return;
    audio.src = src;
    audio.preload = "metadata";
    if (autoplay.current) { autoplay.current = false; void play(audio); }
    return () => audio.pause();
  }, [audio, src]);
  const toggle = useCallback(() => {
    if (audio.paused) { void play(audio); return; }
    audio.pause();
  }, [audio]);
  const playTrack = useCallback(nextIndex => {
    if (nextIndex < 0 || nextIndex >= count) return;
    if (nextIndex === index) { toggle(); return; }
    autoplay.current = true;
    setIndex(nextIndex);
  }, [count, index, toggle]);
  const seek = useCallback(value => { audio.currentTime = value; }, [audio]);
  return { tracks, track, index, ...state, volume, muted, toggle, next, prev, seek, playTrack, setVolume, setMuted };
};